import { events$, loading$ } from '@shared/services'

import { logInfo } from '@shared/helpers/Logger'

import { EVENT_SESSION } from '@shared/config/events'

import basketStore from '@shared/store/basket'
import sessionStore from '@shared/store/session'
import useBasket from '@shared/hooks/useBasket'

// GLOBAL DATA
const consoleColor = 'color: green'

export default function useSession() {
  // METHODS

  async function autostart(payload = {}, forcedOverwrite = false) {
    const { createDefaultBasket } = useBasket()

    // session is active, just return data
    // can only be executed, if autostart is triggered manually
    if (sessionStore.session.active && forcedOverwrite === false) {
      return sessionStore.getSession()
    } else {
      const defaultBasket = await createDefaultBasket(payload)

      const dataFromStorage = sessionStore.getSession()
      let basket

      if (!dataFromStorage.hot && dataFromStorage.updateDate && window.ineum) {
        window.ineum('reportError', 'basket_id_already_available')
      }

      // resume session from localStorage.
      // @NOTE ((dataFromStorage.hot || dataFromStorage.updateDate) is a workaround, to fix issue with basket_id_already_available)
      if (dataFromStorage && (dataFromStorage.hot || dataFromStorage.updateDate) && !forcedOverwrite) {
        const { updateLanguage } = useBasket()

        // try to resume. if it fails, restart
        try {
          basket = await resumeSession(dataFromStorage)
        } catch {
          return autostart({}, true)
        }

        Object.assign(basket, payload)

        await updateLanguage({ app: window._appName, language: basket.language })
        await sessionStore.setSession(basket)
        await basketStore.persistBasket(basket)
      } else {
        // create local basket-session
        if (!forcedOverwrite) {
          basket = Object.assign({}, defaultBasket, dataFromStorage)

          await sessionStore.setSession(basket, { hot: false })
          await basketStore.persistBasket(basket)

          // create 'live' basket-session
        } else {
          basket = await basketStore.createOvpBasket(defaultBasket)
        }
      }

      return basket
    }
  }

  /**
   * try resuming a session, either from localstorage (hot:false), or from the backend(hot:true)
   * @note: at this step, we don't have the basket from the be yet, so you need to check the state from the localStorage
   */
  async function resumeSession(payload) {
    const { basketId, jwToken } = payload

    let _basket
    if (!payload.hot) {
      _basket = payload
    } else {
      _basket = await basketStore.getBasket({ basketId, jwToken }, true)
    }

    if (!_basket) {
      loading$.failed()
      throw new Error(`failed to load basket ${basketId}`)
    }

    logInfo(['%cRESUME SESSION', consoleColor, basketId])
    events$.emit(EVENT_SESSION.RESUME, basketId)
    return _basket
  }

  return {
    autostart,
    resumeSession,
  }
}
